'use client';

import { useState, useEffect, useCallback, useRef } from 'react';

type AuthStep = 'email' | 'otp' | 'phone-collect' | 'success';

interface UserInfo {
  exists: boolean;
  firstName?: string;
  maskedPhone?: string;
}

export interface ParticipantProfile {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  username: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  dateOfBirth: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  county: string | null;
  postcode: string | null;
  country: string | null;
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  emergencyContactRelation: string | null;
  tShirtSize: string | null;
  medicalConditions: string | null;
  clubAffiliation: string | null;
}

export function useParticipantAuth(options?: { onSuccess?: (isNewUser: boolean) => void }) {
  const [step, setStep] = useState<AuthStep>('email');
  const [email, setEmail] = useState('');
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [otpValue, setOtpValue] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isNewUser, setIsNewUser] = useState(false);
  const [participant, setParticipant] = useState<ParticipantProfile | null>(null);
  const [phone, setPhone] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // OTP delivery info from server - determines how to verify
  const [otpChannel, setOtpChannel] = useState<'sms' | 'email'>('email');
  const [verifyIdentifier, setVerifyIdentifier] = useState('');
  const [maskedDestination, setMaskedDestination] = useState('');

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Debounced email check - silently checks if account exists as user types
  // Only fires when email looks complete (has @ and at least 2 chars after the last dot)
  const debouncedCheckEmail = useCallback((emailValue: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = emailValue.trim();
    if (!trimmed || !trimmed.includes('@')) {
      setUserInfo(null);
      return;
    }
    // Require at least 2 characters after the last dot (e.g. .ie, .com)
    const lastDot = trimmed.lastIndexOf('.');
    if (lastDot === -1 || trimmed.length - lastDot - 1 < 2) {
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch('/api/participant/auth/check-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: trimmed }),
        });
        if (res.ok) {
          const data = await res.json();
          setUserInfo(data);
        }
      } catch {
        // Silent - don't show errors for background check
      }
    }, 800);
  }, []);

  // Handle email change with debounced check
  const handleEmailChange = useCallback((value: string) => {
    setEmail(value);
    debouncedCheckEmail(value);
  }, [debouncedCheckEmail]);

  // Force an immediate account lookup, bypassing the 800ms debounce. Used when
  // the user hits Enter before the debounced check has fired - without this,
  // the form was stalling because handleEmailSubmit relied on userInfo being
  // populated and it wasn't yet.
  const lookupAccountNow = useCallback(async (emailValue: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = emailValue.trim();
    if (!trimmed || !trimmed.includes('@')) return null;
    try {
      const res = await fetch('/api/participant/auth/check-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      setUserInfo(data);
      return data as UserInfo;
    } catch {
      return null;
    }
  }, []);

  // Submit email - server decides whether to send SMS or email OTP
  const submitEmail = useCallback(async (emailValue: string) => {
    const trimmed = emailValue.trim();
    if (!trimmed) return;
    setError('');
    setLoading(true);

    try {
      const res = await fetch('/api/participant/auth/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: trimmed, type: 'email' }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Failed to send code');
        return;
      }

      setEmail(trimmed);
      setOtpChannel(data.channel || 'email');
      setVerifyIdentifier(data.verifyIdentifier || trimmed);
      setMaskedDestination(data.maskedDestination || trimmed);
      setResendCooldown(30);
      setStep('otp');
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Verify OTP - uses the identifier the server told us to verify against
  const verifyOTP = useCallback(async (code: string) => {
    setError('');
    setLoading(true);

    try {
      const res = await fetch('/api/participant/auth/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identifier: verifyIdentifier,
          type: otpChannel,
          code,
          createAccount: true,
          // Pass email so account creation and registration claiming use the email
          email: email,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        if (res.status === 429) {
          setError('Too many attempts. Please request a new code.');
          setOtpValue('');
          return;
        }
        setError(data.error || 'Invalid code');
        setOtpValue('');
        return;
      }

      const newUser = data.isNewUser === true;
      setIsNewUser(newUser);

      // Fetch the authenticated participant profile
      const meRes = await fetch('/api/participant/auth/me');
      if (meRes.ok) {
        const meData = await meRes.json();
        setParticipant(meData.participant);
      }

      if (newUser) {
        // New users: collect phone number before completing
        setStep('phone-collect');
      } else {
        setStep('success');
        options?.onSuccess?.(newUser);
      }
    } catch {
      setError('Connection error. Please try again.');
      setOtpValue('');
    } finally {
      setLoading(false);
    }
  }, [verifyIdentifier, otpChannel, email, options]);

  // Resend OTP
  // Resolves true only when the server actually sent a new code, so the
  // screen never says "Sent again" after a rate-limited or failed resend.
  const resendOTP = useCallback(async (): Promise<boolean> => {
    if (resendCooldown > 0) return false;
    setError('');
    setLoading(true);

    try {
      const res = await fetch('/api/participant/auth/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: email, type: 'email' }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(res.status === 429
          ? 'Too many codes requested. Wait a minute and try again.'
          : (data.error || 'Failed to resend code'));
        return false;
      }

      if (data.verifyIdentifier) setVerifyIdentifier(data.verifyIdentifier);
      if (data.channel) setOtpChannel(data.channel);
      if (data.maskedDestination) setMaskedDestination(data.maskedDestination);

      setResendCooldown(30);
      setOtpValue('');
      return true;
    } catch {
      setError('Failed to resend code');
      return false;
    } finally {
      setLoading(false);
    }
  }, [email, resendCooldown]);

  // Submit phone number (new user sign-up)
  const submitPhone = useCallback(async (phoneValue: string) => {
    const trimmed = phoneValue.trim();
    if (!trimmed) return;
    setError('');
    setLoading(true);

    try {
      const res = await fetch('/api/participant/hub/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: trimmed }),
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.error || 'Failed to save phone number');
        return;
      }

      // Update local participant state
      setParticipant((prev) => prev ? { ...prev, phone: trimmed } : prev);
      setStep('success');
      options?.onSuccess?.(true);
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [options]);

  // Skip phone collection
  const skipPhone = useCallback(() => {
    setStep('success');
    options?.onSuccess?.(true);
  }, [options]);

  // Reset to start
  const reset = useCallback(() => {
    setStep('email');
    setEmail('');
    setUserInfo(null);
    setOtpValue('');
    setError('');
    setIsNewUser(false);
    setParticipant(null);
    setOtpChannel('email');
    setVerifyIdentifier('');
    setMaskedDestination('');
    setPhone('');
  }, []);

  return {
    // State
    step,
    email,
    phone,
    userInfo,
    otpValue,
    error,
    loading,
    resendCooldown,
    isNewUser,
    participant,
    otpChannel,
    maskedDestination,
    // Setters
    setEmail: handleEmailChange,
    setOtpValue,
    setPhone,
    setStep,
    // Actions
    submitEmail,
    verifyOTP,
    resendOTP,
    submitPhone,
    skipPhone,
    reset,
    lookupAccountNow,
  };
}
